import React, { useEffect, useState } from "react";
import { Button, Input, Space, Spin, App } from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
} from "@ant-design/icons";
import styles from "./styles.module.scss";

export interface InlineTextEditProps {
  /** Current persisted value (usually the row's title). */
  value: string;
  /**
   * Persists the trimmed value; must reject/throw on failure (e.g. the
   * `.unwrap()` of an RTK Query mutation).
   */
  onSave: (next: string) => Promise<unknown>;
  /** Accessible label for the pencil button. */
  editLabel: string;
  successMessage?: string;
  errorMessage?: string;
}

/**
 * Discreet inline editor for short text shown in list cells: a pencil that
 * appears on hover opens a small input (Enter/check confirms, Esc/close
 * cancels). Every click stops propagation so surrounding anchors or row
 * handlers never navigate while editing.
 */
const InlineTextEdit: React.FC<InlineTextEditProps> = ({
  value,
  onSave,
  editLabel,
  successMessage = "Título actualizado.",
  errorMessage = "No se pudo guardar el cambio.",
}) => {
  const { message } = App.useApp();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  // Shown after a successful save until the list refetch delivers the fresh
  // row, so the cell never flashes back to the stale title.
  const [savedValue, setSavedValue] = useState<string | null>(null);

  useEffect(() => {
    // The fresh value arrived from the server: drop the optimistic copy.
    setSavedValue(null);
  }, [value]);

  const displayValue = savedValue ?? value;

  const startEditing = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setDraft(displayValue);
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setDraft(displayValue);
  };

  const confirm = async () => {
    const trimmed = draft.trim();
    if (!trimmed) {
      message.warning("El título no puede estar vacío.");
      return;
    }
    if (trimmed === displayValue) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(trimmed);
      message.success(successMessage);
      setSavedValue(trimmed);
      setEditing(false);
    } catch (error: any) {
      // Keep the typed text so the user can retry without losing work.
      message.error(error?.data?.message || errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <span className={styles.display}>
        <span>{displayValue}</span>
        <Button
          type="text"
          size="small"
          className={styles.editButton}
          icon={<EditOutlined />}
          aria-label={editLabel}
          onClick={startEditing}
        />
      </span>
    );
  }

  return (
    <Space.Compact
      className={styles.editor}
      onClick={(event) => event.stopPropagation()}
    >
      <Input
        size="small"
        autoFocus
        data-testid="inline-text-edit-input"
        value={draft}
        disabled={saving}
        suffix={saving ? <Spin size="small" /> : <span />}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") confirm();
          if (event.key === "Escape") cancelEditing();
        }}
      />
      <Button
        size="small"
        type="primary"
        icon={<CheckOutlined />}
        aria-label="Guardar"
        disabled={saving}
        onClick={confirm}
      />
      <Button
        size="small"
        icon={<CloseOutlined />}
        aria-label="Cancelar"
        disabled={saving}
        onClick={cancelEditing}
      />
    </Space.Compact>
  );
};

export default InlineTextEdit;
